import { Injectable, computed, signal } from '@angular/core';
import { DARK_COLORS, LIGHT_COLORS } from '../utils/theme-colors';
import { TrackingService } from './tracking.service';
import { themeChanged } from '../../customer-shared/tracking/events';

export type ThemeMode = 'light' | 'dark' | 'system';

// Must match the inline script in index.html, which applies the theme before Angular starts (no flash).
const STORAGE_KEY = 'theme';
// Light until the user picks Dark or System in the settings menu.
const DEFAULT_MODE: ThemeMode = 'light';

// Light / dark / follow the device. Saved per browser; dark mode is the `dark-theme` class on <html>,
// which styles.scss turns into the dark Material theme and the dark --glimpse-* colors.
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly systemDark = window.matchMedia('(prefers-color-scheme: dark)');
  readonly mode = signal<ThemeMode>(this.readMode());
  // For colors set in code (charts), which CSS can't switch.
  readonly isDark = signal(false);
  readonly colors = computed(() => (this.isDark() ? DARK_COLORS : LIGHT_COLORS));

  constructor(private tracking: TrackingService) {
    this.apply();
    this.systemDark.addEventListener('change', () => this.apply());
  }

  setMode(mode: ThemeMode) {
    const previous = this.mode();
    if (mode === previous) return;
    this.mode.set(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {}
    this.apply();
    this.tracking.track(themeChanged({ theme: mode, previous, shown: this.isDark() ? 'dark' : 'light' }));
  }

  private readMode(): ThemeMode {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
    } catch {}
    return DEFAULT_MODE;
  }

  private apply() {
    const mode = this.mode();
    const dark = mode === 'dark' || (mode === 'system' && this.systemDark.matches);
    document.documentElement.classList.toggle('dark-theme', dark);
    this.isDark.set(dark);
  }
}
